import { Component } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { smartDateString } from '../../../../utils/date';
import { InfoContainer, InfoPart } from './styles';
import Collapsible from '../../../Collapsible/Collapsible';
import { blue, lightYellow, red, white } from '../../../../utils/style/colors';
import Button from '../../../Button/Button';
import {
  removeVersion,
  removeVersionFn,
  updateItem,
  updateItemFn,
} from 'store/providers/providerSlice';
import { notifyError } from '../../../Notify/Notify';
import { cloneDeep } from 'lodash';
import Icon from '../../../Icon/Icon';
import { appDispatch, IUsers, RootState } from 'store/store';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  IProviderDocumentViewObj,
  IProviderItemView,
  IProviderItemViewWithData,
} from 'store/providers/providerTypes';
import { WithTranslation, withTranslation } from 'react-i18next';

interface IStateToProps {
  users: IUsers['map'];
}

interface IDispatchToProps {
  requireSeveralUser: (
    membersId: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  updateItem: (item: IProviderItemView) => ReturnType<typeof updateItemFn>;
  removeVersion: (
    item: IProviderItemView,
    versionId: number
  ) => ReturnType<typeof removeVersionFn>;
}
type IOwnProps = {
  itemWithData: IProviderItemViewWithData;
  style: any;
  item: IProviderDocumentViewObj;
  viewingVersion: number;
  changeViewingVersion: (version: number) => any;
};

type IComponentProps = IStateToProps &
  IDispatchToProps &
  IOwnProps &
  WithTranslation;
interface IComponetState {
  saving: boolean;
}
class DocumentVersions extends Component<IComponentProps, IComponetState> {
  state = {
    saving: false,
  };
  componentDidMount() {
    const { item, users } = this.props;
    const notFoundUsers = [] as number[];
    item.versions.forEach((v) => {
      const member = users[v.creatorId];
      if (!member || (Object.keys(member).length === 0 && v.creatorId)) {
        notFoundUsers.push(v.creatorId);
      }
    });
    if (notFoundUsers.length > 0) {
      this.props.requireSeveralUser(notFoundUsers);
    }
  }

  setActiveVersion = (versionId: number) => {
    const { itemWithData, item } = this.props;
    const newItemWithDate = cloneDeep(itemWithData);
    const newItem = cloneDeep(item);
    newItem.currentDocumentId = versionId;
    newItemWithDate.data = newItem;
    this.setState({ saving: true });
    this.props.updateItem(newItem).then(
      () => {
        this.setState({ saving: false });
      },
      (error) => {
        notifyError(error);
        this.setState({ saving: false });
      }
    );
  };
  removeVersion = (versionId: number) => {
    const { itemWithData } = this.props;
    if (confirm(this.props.t('confirmRemoveVersion'))) {
      this.setState({ saving: true });
      this.props.removeVersion(itemWithData.data, versionId).then(
        () => {
          this.setState({ saving: false });
        },
        (error) => {
          notifyError(error);
          this.setState({ saving: false });
        }
      );
    }
  };
  render() {
    const { item, users, style, viewingVersion } = this.props;
    const versions = [...item.versions];

    return (
      <Collapsible header={<div>{this.props.t('documentHistory')}</div>}>
        <div style={style}>
          {versions
            .sort((a, b) => b.creationDate - a.creationDate)
            .filter((a) => !a.document.deleted)
            .map((version) => {
              const selected = viewingVersion === version.document.id;
              const current = item.currentDocumentId === version.document.id;
              return (
                <InfoContainer
                  key={version.document.id}
                  background={selected ? lightYellow : white}
                  active={current}
                >
                  <InfoPart>
                    <b>{this.props.t('uploadDate')}: </b>
                    <span>{smartDateString(version.creationDate, true)}</span>
                  </InfoPart>
                  <InfoPart>
                    <b>{this.props.t('creatorUser')}</b>
                    {users?.[version.creatorId]?.name || ''}
                  </InfoPart>
                  <InfoPart>
                    <b>{this.props.t('versionName')}</b>
                    <span>{version.properties.name}</span>
                  </InfoPart>
                  <InfoPart
                    style={{ minWidth: '200px', alignItems: 'flex-start' }}
                  >
                    <b>{this.props.t('versionMessage')}</b>
                    <span>{version.properties.message}</span>
                  </InfoPart>
                  <div
                    style={{
                      margin: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    {!selected && (
                      <Button
                        onClick={() => {
                          this.props.changeViewingVersion(version.document.id!);
                        }}
                      >
                        <Icon type="eye" color={blue} />
                      </Button>
                    )}
                    {!current &&
                      selected &&
                      item.editable &&
                      !item.upstreamItemId && (
                        <Button
                          onClick={() => {
                            this.setActiveVersion(version.document.id!);
                          }}
                        >
                          {this.props.t('setActive')}{' '}
                        </Button>
                      )}
                    {!current && item.editable && !item.upstreamItemId && (
                      <Button
                        onClick={() => {
                          this.removeVersion(version.document.id!);
                        }}
                      >
                        <Icon type="trash" color={red} />
                      </Button>
                    )}
                  </div>
                </InfoContainer>
              );
            })}
        </div>
      </Collapsible>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const users = state.users.map;
  return { users };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  requireSeveralUser: (membersId: number[]) =>
    appDispatch(requireSeveralUser({ usersList: membersId })),
  updateItem: (item: any) => appDispatch(updateItem({ item })),
  removeVersion: (item: any, versionId: number) =>
    appDispatch(removeVersion({ item, version: versionId })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation('modals')(DocumentVersions));
